import { useCallback, useEffect, useRef, useState } from "react";

export interface UseChatPollOptions {
  /** Cadence while the surface has focus. Defaults to 5s per the plan's SLA cadence. */
  focusedIntervalMs?: number;
  /** Cadence while visible but not focused (app open elsewhere). Defaults to 45s (30-60s band). */
  backgroundIntervalMs?: number;
  /** Cap on the exponential backoff applied after consecutive poll failures. */
  maxBackoffMs?: number;
}

export interface UseChatPollResult {
  poll: () => void;
  lastError: Error | null;
}

const DEFAULT_FOCUSED_MS = 5_000;
const DEFAULT_BACKGROUND_MS = 45_000;
const DEFAULT_MAX_BACKOFF_MS = 60_000;

/**
 * Polling cadence, isolated from chat specifics so it's reusable. Stops
 * entirely when the document is hidden (backgrounded tab/app) rather than
 * just slowing down, since there's nothing to show the user in that state.
 */
export function useChatPoll(fn: () => Promise<void>, opts: UseChatPollOptions = {}): UseChatPollResult {
  const { focusedIntervalMs = DEFAULT_FOCUSED_MS, backgroundIntervalMs = DEFAULT_BACKGROUND_MS, maxBackoffMs = DEFAULT_MAX_BACKOFF_MS } = opts;

  const [lastError, setLastError] = useState<Error | null>(null);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const consecutiveErrorsRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runAndSchedule = useCallback(
    (immediate: boolean) => {
      clearTimer();
      if (document.visibilityState === "hidden") {
        return;
      }

      const schedule = () => {
        if (document.visibilityState === "hidden") {
          return;
        }
        const backoff = consecutiveErrorsRef.current > 0 ? Math.min(focusedIntervalMs * 2 ** consecutiveErrorsRef.current, maxBackoffMs) : 0;
        const baseInterval = document.hasFocus() ? focusedIntervalMs : backgroundIntervalMs;
        const delay = Math.max(baseInterval, backoff);
        timerRef.current = setTimeout(tick, delay);
      };

      const tick = () => {
        if (inFlightRef.current) {
          schedule();
          return;
        }
        inFlightRef.current = true;
        fnRef.current()
          .then(() => {
            consecutiveErrorsRef.current = 0;
            setLastError(null);
          })
          .catch((err: unknown) => {
            consecutiveErrorsRef.current += 1;
            setLastError(err instanceof Error ? err : new Error("Poll failed"));
          })
          .finally(() => {
            inFlightRef.current = false;
            schedule();
          });
      };

      if (immediate) {
        tick();
      } else {
        schedule();
      }
    },
    [clearTimer, focusedIntervalMs, backgroundIntervalMs, maxBackoffMs],
  );

  const poll = useCallback(() => {
    runAndSchedule(true);
  }, [runAndSchedule]);

  useEffect(() => {
    runAndSchedule(true);

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearTimer();
      } else {
        runAndSchedule(true);
      }
    };
    const onFocus = () => runAndSchedule(true);

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { poll, lastError };
}
